import React, { useState, useEffect, useRef } from 'react';

import SelectOptionArray from 'types/model/selectOption';

import Grid from 'components/elements/Grid';

import {
  ContractSelectGroup,
  ContractSearchSelectGroup,
  ContractTextInput,
} from './ContractFields';
import { IField } from 'components/shared/form/IField';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import { usePriceContext } from 'contracts/utils/priceContext';
import { GaugeChart } from 'shared/GaugeChart';
import ConfirmationOverlay from 'shared/ConfirmationOverlay';

interface CoffeeDetailsProps {
  fields: IField[];
  opportunityEstimatedKg?: string;
  packagingTypeOptions: SelectOptionArray;
  markOptions: SelectOptionArray;
  onMarkChange: (value: string) => void;
  markIdField?: IField;
  markId: string;
  qualityIdField?: IField;
  qualityId: string;
  onQualityChange: (value: string) => void;
  preparationIdField?: IField;
  preparationId: string;
  setPreparationId: (value: string) => void;
  categoryIdField?: IField;
  profileIdField?: IField;
  excelsoEstimateQualityIdField?: IField;
  countryId?: string | number;
  contractType?: string;
}

const CoffeeDetails = ({
  preparationIdField,
  preparationId,
  setPreparationId,
  opportunityEstimatedKg,
  fields,
  packagingTypeOptions,
  markOptions,
  onMarkChange,
  markIdField,
  markId,
  qualityIdField,
  qualityId,
  onQualityChange,
  categoryIdField,
  profileIdField,
  excelsoEstimateQualityIdField,
  countryId,
  contractType,
}: CoffeeDetailsProps) => {
  const packagingTypeIdField = fields.find((field) => field.name == 'packaging_type_id');
  const [packagingTypeId, setPackagingTypeId] = useState(
    packagingTypeIdField?.value || '0'
  );
  const { selectedPackagingTypeCapacity } = usePriceContext();
  const { units } = usePriceContext();

  const weight =
    parseInt(units?.value || '0', 10) *
    parseFloat(selectedPackagingTypeCapacity?.value || '0');
  const [calculatedWeight, setCalculatedWeight] = useState(weight);

  useEffect(() => {
    const capacity = packagingTypeIdField?.options?.find(
      (option) => option.id == packagingTypeId
      // @ts-ignore: Wrongly typed Options
    )?.capacity;

    selectedPackagingTypeCapacity?.set(capacity);
  }, [packagingTypeId]);

  useEffect(() => {
    const parsedUnits = parseInt(units?.value || '0', 10);
    const parsedSelectedPackagingTypeCapacity = parseFloat(
      selectedPackagingTypeCapacity?.value || '0'
    );
    setCalculatedWeight(parsedSelectedPackagingTypeCapacity * parsedUnits);
  }, [units, selectedPackagingTypeCapacity]);

  const handlePackagingTypeIdChange = (selectedPackagingTypeId: number) => {
    const capacity = packagingTypeIdField?.options?.find(
      (option) => option.id == selectedPackagingTypeId
      // @ts-ignore: Wrongly typed Options
    )?.capacity;

    if (opportunityEstimatedKg && units) {
      const estimatedKg = parseInt(opportunityEstimatedKg, 10);
      const packagingCapacity = parseFloat(capacity);
      const newUnits = Math.ceil(estimatedKg / packagingCapacity);

      units.set(newUnits.toString());
      setCalculatedWeight(newUnits * capacity);
    }
    setPackagingTypeId(selectedPackagingTypeId.toString());
    selectedPackagingTypeCapacity?.set(capacity);
  };

  const [availableWeight, setAvailableWeight] = useState<number | null>(null);
  const [totalWeight, setTotalWeight] = useState<number | null>(null);
  const [inventoryQueried, setInventoryQueried] = useState(false);
  const [excelsoEstimateQualityId, setExcelsoEstimateQualityId] = useState(
    excelsoEstimateQualityIdField?.value || ''
  );
  const [showExcelsoConfirmation, setShowExcelsoConfirmation] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    if (
      contractType !== 'FwdContract' ||
      !excelsoEstimateQualityId ||
      availableWeight === null ||
      !calculatedWeight ||
      Number(calculatedWeight) <= availableWeight
    ) {
      return;
    }

    const form = document.querySelector<HTMLFormElement>('form');
    formRef.current = form;

    const handleSubmit = (e: Event) => {
      e.preventDefault();
      setShowExcelsoConfirmation(true);
    };

    form?.addEventListener('submit', handleSubmit);
    return () => form?.removeEventListener('submit', handleSubmit);
  }, [availableWeight, excelsoEstimateQualityId, calculatedWeight]);

  const handleConfirm = (_e: React.MouseEvent) => {
    setShowExcelsoConfirmation(false);
    setTimeout(() => formRef.current?.submit(), 0);
  };

  const handleCancel = (_e: React.MouseEvent) => {
    setShowExcelsoConfirmation(false);
    const submitButton =
      formRef.current?.querySelector<HTMLInputElement>('[type="submit"]');
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.removeAttribute('data-disabled');
    }
  };

  useEffect(() => {
    // Excelso availability is keyed only by quality + country, so the gauge must show
    // whenever a quality is selected — not only when a profile/category is present
    // (PLA-1001 v3). Previously gated on categoryIdField visibility, which hid the gauge
    // for quality-only contracts.
    if (!qualityId || !countryId) {
      setInventoryQueried(false);
      return;
    }

    setInventoryQueried(true);
    let isMounted = true;
    fetch(
      `/api/v1/excelso_estimate_qualities?quality_id=${qualityId}&country_id=${countryId}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (isMounted) {
          const total = data.total_weight != null ? Number(data.total_weight) : null;
          const committed =
            data.committed_contract_kg != null ? Number(data.committed_contract_kg) : 0;
          // Show inventory available for new contracts: total minus already-committed
          // (non-cancelled) contracts. Matches Contracts::PostCreateService routing.
          setAvailableWeight(total != null ? total - committed : null);
          setTotalWeight(total);
          setExcelsoEstimateQualityId(
            data.excelso_estimate_quality_id != null
              ? String(data.excelso_estimate_quality_id)
              : ''
          );
        }
      })
      .catch(() => {
        if (isMounted) {
          setAvailableWeight(null);
          setTotalWeight(null);
          setExcelsoEstimateQualityId('');
        }
      });
    return () => {
      isMounted = false;
    };
  }, [qualityId, countryId, opportunityEstimatedKg]);

  const normField = fields.find((field) => field.name == 'norm_id');
  const certificateIdField = fields.find((field) => field.name == 'certificate_id');
  const processTypeIdField = fields.find((field) => field.name == 'process_type_id');
  // PLA-1066: la variedad es opcional, por eso va sin `required`.
  const varietyIdField = fields.find((field) => field.name == 'variety_id');

  const subtitle = opportunityEstimatedKg
    ? I18n.translate('contracts.form.estimated_kg_hint', {
        weight: opportunityEstimatedKg,
      })
    : '';

  // TODO When a production order exists, packaging type and units cannot be edited anymore
  // We still have to send the proper contract values when field is disabled, current fallback sends the name instead of id
  const hiddenFields = fields
    .filter((field) => {
      return ['packaging_type_id', 'units'].includes(field.name) && !field.enabled;
    })
    .map((hiddenField, index) => (
      <input
        key={index}
        type='hidden'
        name={`contract[${hiddenField.name}]`}
        value={hiddenField.value}
      />
    ));

  return (
    <>
      <FieldSetWithTitle
        title={I18n.translate('contracts.form.coffee_details')}
        subtitle={subtitle}
      >
        <Grid grid={12}>
          <ContractSelectGroup
            field={{
              ...packagingTypeIdField,
              options: packagingTypeIdField?.options?.sort((a, b) =>
                a.name.localeCompare(b.name)
              ),
            }}
            value={packagingTypeId}
            onChange={handlePackagingTypeIdChange}
            overrideOptions={packagingTypeOptions}
            required
            showAsterisk={false}
          />
          <ContractTextInput
            field={units?.field}
            value={units?.value}
            onChange={units?.set}
            append='#'
            info={calculatedWeight ? `${calculatedWeight} KG` : '-'}
            type='number'
            min={1}
          />
          {/* Profile + category inherited from the opportunity, shown read-only
              right below Capacity and Packaging / Estimated units (PLA-1001). */}
          {categoryIdField?.visible !== false && categoryIdField && (
            <ContractSelectGroup field={categoryIdField} />
          )}
          {profileIdField?.visible !== false && profileIdField && (
            <ContractSelectGroup
              field={{
                ...profileIdField,
                options: (profileIdField.options || []).map((o: any) => {
                  const isActive = o?.active !== false;
                  return isActive
                    ? o
                    : { ...o, name: `${o.name} (${I18n.translate('common.inactive')})` };
                }),
              }}
            />
          )}
          <ContractSelectGroup
            field={qualityIdField}
            onChange={onQualityChange}
            value={qualityId}
          />
          {inventoryQueried &&
            (totalWeight !== null &&
            !isNaN(totalWeight) &&
            totalWeight > 0 &&
            availableWeight !== null &&
            !isNaN(availableWeight) &&
            availableWeight >= 0 ? (
              <GaugeChart value={availableWeight} total={totalWeight} />
            ) : (
              <span className='text--error' style={{ fontWeight: 'bold' }}>
                {I18n.translate('opportunities.form.no_available_inventory')}
              </span>
            ))}
          {excelsoEstimateQualityIdField?.visible !== false &&
            excelsoEstimateQualityIdField && (
              <input
                type='hidden'
                name='contract[excelso_estimate_quality_id]'
                value={excelsoEstimateQualityId}
              />
            )}
          <ContractSearchSelectGroup
            field={markIdField}
            overrideOptions={markOptions}
            onChange={onMarkChange}
            value={markId}
            acceptEmptyValue={true}
          />
          <ContractSelectGroup
            field={{
              ...normField,
              options: normField?.options?.sort((a, b) => a.name.localeCompare(b.name)),
            }}
            required
            showAsterisk={false}
          />
          <ContractSelectGroup
            field={{
              ...certificateIdField,
              options: certificateIdField?.options?.sort((a, b) =>
                a.name.localeCompare(b.name)
              ),
            }}
          />
          <ContractSelectGroup field={processTypeIdField} required showAsterisk={false} />

          <ContractSelectGroup
            field={{
              ...varietyIdField,
              options: varietyIdField?.options?.sort((a, b) =>
                a.name.localeCompare(b.name)
              ),
            }}
          />

          <ContractSelectGroup
            field={preparationIdField}
            value={preparationId}
            onChange={setPreparationId}
            required
            showAsterisk={false}
          />
          {hiddenFields}
        </Grid>
      </FieldSetWithTitle>
      {showExcelsoConfirmation && (
        <ConfirmationOverlay
          title={I18n.translate('opportunities.form.exceeded_excelso_limit_confirmation')}
          confirmText={I18n.translate(
            'opportunities.form.exceeded_excelso_limit_confirm'
          )}
          cancelText={I18n.translate('opportunities.form.exceeded_excelso_limit_cancel')}
          onConfirmClick={handleConfirm}
          onCancelClick={handleCancel}
        />
      )}
    </>
  );
};

export default CoffeeDetails;
